import { Injectable, computed, inject, signal } from '@angular/core';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';
import { API_ENDPOINTS } from '../api/endpoints';
import { Congress, CongressId, mapCongresList } from '../models/congress.model';
import { AuthService } from './auth.service';

@Injectable({ providedIn: 'root' })
export class CongressSelectorService {
  private readonly http = inject(HttpClient);
  private readonly auth = inject(AuthService);

  private readonly _available = signal<Congress[]>([]);
  private readonly _current = signal<CongressId | null>(null);

  readonly available = this._available.asReadonly();
  readonly current = this._current.asReadonly();
  readonly id = computed(() => this._current());
  readonly selected = computed(
    () => this._available().find((c) => c.id === this._current()) ?? null,
  );
  /** Édition à ouvrir par défaut : la courante, sinon la première de la liste. */
  readonly defaultId = computed(
    () => this._available().find((c) => c.isCurrent)?.id ?? this._available()[0]?.id ?? null,
  );

  private loaded = false;

  /**
   * Charge la liste des congrès depuis l'API (idempotent après un succès). Un échec laisse la
   * liste vide et non chargée ; une panne du back (5xx, réseau) le déclare injoignable, pour que
   * les guards mènent à /unavailable au lieu de relancer l'appel.
   */
  async load(): Promise<void> {
    if (this.loaded) {
      return;
    }
    try {
      const body = await firstValueFrom(this.http.get<unknown>(API_ENDPOINTS.congres.list()));
      this._available.set(mapCongresList(body));
      this.loaded = true;
    } catch (e) {
      this._available.set([]);
      if (e instanceof HttpErrorResponse && (e.status === 0 || e.status >= 500)) {
        this.auth.markBackendUnreachable();
      }
    }
  }

  select(id: CongressId): void {
    this._current.set(id);
  }
}
